import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Table, Form, InputGroup } from "react-bootstrap";
import AddPackageModal from "./AddPackageModal";
import PackageTableEdit from "./PackageTableEdit";
import {
    collection,
    getDocs,
    doc,
    setDoc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    query
} from "firebase/firestore";
import { db, auth } from "../firebase";
import axios from "axios";
import Filter from "./Filter";
import NextPrevPagination from "./NextPrevPagination";
import { FaEdit, FaTrash, FaSearch } from "react-icons/fa";

const PackageTable = () => {
    const [allPackages, setAllPackages] = useState([]);
    const [countries, setCountries] = useState([]);
    const [operators, setOperators] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState("");
    const [selectedOperator, setSelectedOperator] = useState("");
    const [localSearch, setLocalSearch] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [order, setOrder] = useState("desc");
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [loadingOperators, setLoadingOperators] = useState(false);
    const itemsPerPage = 20;
    const [editPackageData, setEditPackageData] = useState(null);

    // Debounce search input to avoid excessive Firestore reads
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearchQuery(localSearch);
        }, 500);
        return () => clearTimeout(handler);
    }, [localSearch]);

    // ── Fetch enabled countries from Firestore ──
    const fetchCountries = async () => {
        try {
            const snapshot = await getDocs(collection(db, "countries"));
            const list = snapshot.docs
                .map((d) => ({ docId: d.id, ...d.data() }))
                .filter((c) => c.enabled)
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            setCountries(list);
        } catch (error) {
            console.error("Error fetching countries:", error);
        }
    };

    // ── Fetch operators for a country (existing API logic) ──
    const fetchOperators = async (countryCode) => {
        try {
            setLoadingOperators(true);
            const user = auth.currentUser;
            if (!user) {
                setLoadingOperators(false);
                return;
            }
            const idToken = await user.getIdToken();
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/fetchOperators/${countryCode}?size=200`,
                {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (response.data.success) {
                setOperators(response.data.data || []);
            } else {
                setOperators([]);
            }
        } catch (error) {
            console.error("Error fetching operators:", error);
            setOperators([]);
        } finally {
            setLoadingOperators(false);
        }
    };

    const fetchAllPackages = async () => {
        try {
            setLoading(true);
            const q = query(collection(db, "packages"));
            const snapshot = await getDocs(q);

            let packageList = [];
            let needsMigration = false;
            const migrationTasks = [];

            for (const d of snapshot.docs) {
                const data = d.data();
                const docId = d.id;

                if (!data.packageId || docId !== data.packageId) {
                    needsMigration = true;
                    const finalPackageId = data.packageId || `pkg_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
                    const newPayload = {
                        ...data,
                        packageId: finalPackageId,
                    };

                    migrationTasks.push((async () => {
                        try {
                            const newDocRef = doc(db, "packages", finalPackageId);
                            await setDoc(newDocRef, newPayload);
                            const oldDocRef = doc(db, "packages", docId);
                            await deleteDoc(oldDocRef);
                            return { docId: finalPackageId, ...newPayload };
                        } catch (err) {
                            console.error(`Failed to migrate package ${docId}:`, err);
                            return { docId, ...data };
                        }
                    })());
                } else {
                    packageList.push({ docId, ...data });
                }
            }

            if (needsMigration) {
                console.log("Migrating packages to use packageId as Document ID...");
                const migratedPackages = await Promise.all(migrationTasks);
                packageList = [...packageList, ...migratedPackages];
            }

            setAllPackages(packageList);
        } catch (error) {
            console.error("Error fetching packages:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleReload = async () => {
        setCurrentPage(1);
        await fetchAllPackages();
    };

    const fetchPackages = () => {
        handleReload();
    };

    useEffect(() => {
        fetchCountries();
        fetchAllPackages();
    }, []);

    // When filters or search changes, reset to the first page
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCountry, selectedOperator, searchQuery, order]);

    // When country filter changes → load operators
    useEffect(() => {
        if (selectedCountry) {
            fetchOperators(selectedCountry);
            setSelectedOperator("");
        } else {
            setOperators([]);
            setSelectedOperator("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCountry]);

    const handleFilterChange = (e) => {
        setOrder(e.target.value);
    };

    // ── Toggle enabled/disabled directly from table ──
    const handleToggleEnabled = async (packageId, currentEnabled) => {
        try {
            const packageRef = doc(db, "packages", packageId);
            const newEnabled = !currentEnabled;
            await updateDoc(packageRef, {
                enabled: newEnabled,
                updatedAt: serverTimestamp(),
            });
            setAllPackages((prev) =>
                prev.map((pkg) =>
                    pkg.packageId === packageId ? { ...pkg, enabled: newEnabled } : pkg
                )
            );
        } catch (error) {
            console.error("Error toggling package status:", error);
        }
    };

    // ── Delete package ──
    const handleDelete = async (packageId) => {
        if (window.confirm("Are you sure you want to delete this package?")) {
            try {
                await deleteDoc(doc(db, "packages", packageId));
                handleReload();
            } catch (error) {
                console.error("Error deleting package:", error);
            }
        }
    };

    // ── Resolve country name from code ──
    const getCountryName = (code) => {
        const country = countries.find((c) => c.code === code);
        return country ? country.name : code || "-";
    };

    const handlePrev = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // ── Filtered & Sorted packages ──
    const filteredPackages = useMemo(() => {
        let list = [...allPackages];

        if (selectedCountry) {
            list = list.filter((pkg) => pkg.countryCode === selectedCountry);
        }

        if (selectedOperator) {
            list = list.filter((pkg) => pkg.operatorId === Number(selectedOperator));
        }

        const qVal = searchQuery.trim().toLowerCase();
        if (qVal) {
            list = list.filter((pkg) => {
                const nameMatch = (pkg.displayName || "").toLowerCase().includes(qVal);
                const operatorMatch = (pkg.operatorName || "").toLowerCase().includes(qVal);
                const codeMatch = (pkg.countryCode || "").toLowerCase().includes(qVal);
                return nameMatch || operatorMatch || codeMatch;
            });
        }

        // Sorting
        list.sort((a, b) => {
            if (qVal) {
                return (a.displayName || "").localeCompare(b.displayName || "");
            } else {
                const timeA = a.createdAt?.seconds || a.createdAt?.toMillis?.() || 0;
                const timeB = b.createdAt?.seconds || b.createdAt?.toMillis?.() || 0;
                return order === "desc" ? timeB - timeA : timeA - timeB;
            }
        });

        return list;
    }, [allPackages, selectedCountry, selectedOperator, searchQuery, order]);

    const totalCount = filteredPackages.length;
    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const currentPackages = filteredPackages.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);

    return (
        <section className="px-1 py-4 nav-body-bg btm-rad">
            <AddPackageModal
                fetchPackages={fetchPackages}
                countries={countries}
            />
            <Container>
                {/* ── Header Row ── */}
                <Row className="align-items-center justify-content-between flex-column flex-md-row text-center text-md-start mb-3">
                    <Col xs="auto" className="mb-3 mb-md-0">
                        <h3 className="text-h2">All Packages</h3>
                    </Col>
                    <Col xs="auto">
                        <div className="d-flex align-items-center gap-2">
                            <Filter value={order} onChange={handleFilterChange} />
                            <button
                                className="btn btn-primary px-3"
                                style={{ padding: "3px 0px" }}
                                data-bs-toggle="modal"
                                data-bs-target="#addPackageModal"
                            >
                                + Add Package
                            </button>
                        </div>
                    </Col>
                </Row>

                {/* ── Filters Row ── */}
                <Row className="align-items-center mb-3 g-2">
                    {/* Country Filter */}
                    <Col md={3}>
                        <Form.Select
                            value={selectedCountry}
                            onChange={(e) => {
                                setSelectedCountry(e.target.value);
                            }}
                            className="transparent-header border"
                            style={{ borderColor: "#3b6353" }}
                        >
                            <option value="">All Countries</option>
                            {countries.map((c) => (
                                <option key={c.code} value={c.code}>
                                    {c.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    {/* Operator Filter */}
                    <Col md={3}>
                        <Form.Select
                            value={selectedOperator}
                            onChange={(e) => {
                                setSelectedOperator(e.target.value);
                            }}
                            disabled={!selectedCountry || loadingOperators}
                            className="transparent-header border"
                            style={{ borderColor: "#3b6353" }}
                        >
                            <option value="">
                                {!selectedCountry
                                    ? "Select Country First"
                                    : loadingOperators
                                    ? "Loading..."
                                    : "All Operators"}
                            </option>
                            {operators.map((op) => (
                                <option key={op.id} value={op.id}>
                                    {op.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    {/* Search */}
                    <Col md={4}>
                    <InputGroup className="theme-search-group">
                            <Form.Control
                                type="text"
                                placeholder="Search by name or country code"
                                className="db-set-style border-0 shadow-none"
                                style={{ backgroundColor: "transparent" }}
                                value={localSearch}
                                onChange={(e) => {
                                    setLocalSearch(e.target.value);
                                }}
                            />
                            <InputGroup.Text className="bg-transparent border-0">
                                <FaSearch style={{ color: "var(--accent-lime)" }} />
                            </InputGroup.Text>
                        </InputGroup>
                    </Col>
                </Row>

                {/* ── Table ── */}
                <div
                    className="table-responsive scrollable-table-y mt-2 bg-transparent"
                    style={{ minHeight: "100vh", overflowY: "auto" }}
                >
                    <style>
                        {`
                            @media (max-width: 768px) {
                                .scrollable-table-y {
                                    max-height: 300px;
                                    overflow-y: auto;
                                }
                            }
                            .form-switch .form-check-input {
                                width: 2.5em;
                                height: 1.25em;
                                cursor: pointer;
                            }
                            .form-switch .form-check-input:checked {
                                background-color: #1A73E8;
                                border-color: #1A73E8;
                            }
                        `}
                    </style>

                    <Table className="table-hover align-middle body-text-two mb-0">
                        <thead
                            className="sticky-top top-0"
                            style={{
                                zIndex: 10,
                                background: "transparent !important",
                            }}
                        >
                            <tr>
                                <th className="transparent-header">SL</th>
                                <th className="text-center transparent-header">
                                    Country
                                </th>
                                <th className="text-center transparent-header">
                                    Operator
                                </th>
                                <th className="text-center transparent-header">
                                    Display Name
                                </th>
                                <th className="text-center transparent-header">
                                    Recipient Amt
                                </th>
                                <th className="text-center transparent-header">
                                    Currency
                                </th>
                                <th className="text-center transparent-header">
                                    Base Price (GBP)
                                </th>
                                <th className="text-center transparent-header">
                                    Type
                                </th>
                                <th className="text-center transparent-header">
                                    Status
                                </th>
                                <th className="text-center transparent-header">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan="10"
                                        className="text-center py-4 transparent-header"
                                    >
                                        <div className="admin-loader"></div>
                                    </td>
                                </tr>
                            ) : currentPackages.length > 0 ? (
                                currentPackages.map((pkg, index) => (
                                    <tr
                                        key={pkg.packageId || index}
                                        style={{
                                            borderBottom: "1px solid #8CB6EF",
                                        }}
                                    >
                                        <td className="py-3 transparent-header text-center">
                                            {indexOfFirstItem + index + 1}
                                        </td>
                                        <td className="py-3 transparent-header text-center">
                                            {getCountryName(pkg.countryCode)}
                                        </td>
                                        <td className="py-3 transparent-header text-center">
                                            {pkg.operatorName || "-"}
                                        </td>
                                        <td className="py-3 transparent-header text-center fw-semibold">
                                            {pkg.displayName || "-"}
                                        </td>
                                        <td className="py-3 transparent-header text-center">
                                            {pkg.recipientAmount ?? "-"}
                                        </td>
                                        <td className="py-3 transparent-header text-center">
                                            {pkg.recipientCurrency?.toUpperCase() ||
                                                 "-"}
                                        </td>
                                        <td className="py-3 transparent-header text-center">
                                            {pkg.basePriceGbp !== undefined
                                                ? `£${Number(
                                                      pkg.basePriceGbp
                                                  ).toFixed(2)}`
                                                : "-"}
                                        </td>
                                        <td className="py-3 transparent-header text-center">
                                            <span
                                                className={`badge ${
                                                    pkg.type === "data"
                                                        ? "bg-info"
                                                        : "bg-secondary"
                                                }`}
                                            >
                                                {pkg.type || "airtime"}
                                            </span>
                                        </td>
                                        <td className="py-3 transparent-header text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                <div className="form-check form-switch mb-0">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        checked={
                                                            pkg.enabled ?? true
                                                        }
                                                        onChange={() =>
                                                            handleToggleEnabled(
                                                                pkg.packageId,
                                                                pkg.enabled ??
                                                                    true
                                                            )
                                                        }
                                                        style={{ margin: 0 }}
                                                    />
                                                </div>
                                                <span
                                                    className={`fw-semibold small ${
                                                        pkg.enabled ?? true
                                                            ? "text-success"
                                                            : "text-danger"
                                                    }`}
                                                    style={{
                                                        width: "65px",
                                                        textAlign: "left",
                                                    }}
                                                >
                                                    {pkg.enabled ?? true
                                                        ? "Enabled"
                                                        : "Disabled"}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3 transparent-header text-center">
                                            <div className="d-flex justify-content-center gap-3">
                                                <FaEdit
                                                    className="text-warning"
                                                    style={{
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() =>
                                                        setEditPackageData(pkg)
                                                    }
                                                />
                                                <FaTrash
                                                    className="text-danger"
                                                    style={{
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() =>
                                                        handleDelete(pkg.packageId)
                                                    }
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="10"
                                        className="text-center py-4 transparent-header"
                                    >
                                        No packages found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* ── Pagination ── */}
                {!loading && filteredPackages.length > 0 && (
                    <NextPrevPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPrev={handlePrev}
                        onNext={handleNext}
                    />
                )}
            </Container>

            {/* ── Edit Modal ── */}
            <PackageTableEdit
                pkg={editPackageData}
                setPkg={setEditPackageData}
                fetchPackages={fetchPackages}
                countries={countries}
            />
        </section>
    );
};

export default PackageTable;
